import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { formatTime12Hour } from '@/utils/calendarHelpers';

interface ClockInOutButtonProps {
  slot: {
    id: string;
    caretaker_id: string;
    date: string;
    start_time: string;
    end_time: string;
    status: string;
    clock_in_time?: string | null;
    clock_out_time?: string | null;
    actual_start_time?: string | null;
    actual_end_time?: string | null;
    actual_hours_worked?: number | null;
    actual_pay_amount?: number | null;
    no_show?: number;
  };
  currentUserId: string;
  isParentOrAdmin: boolean;
}

export function ClockInOutButton({ slot, currentUserId, isParentOrAdmin }: ClockInOutButtonProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState('');

  const isOwner = slot.caretaker_id === currentUserId;
  const isToday = slot.date === new Date().toLocaleDateString('en-CA');
  const isClockedIn = !!slot.clock_in_time && !slot.clock_out_time;
  const isCompleted = !!slot.clock_in_time && !!slot.clock_out_time;
  const isNoShow = !!slot.no_show;

  // Update elapsed time every second when clocked in
  useEffect(() => {
    if (!isClockedIn || !slot.clock_in_time) return;

    const update = () => {
      const start = new Date(slot.clock_in_time!).getTime();
      const now = Date.now();
      const diffMs = now - start;
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      setElapsed(hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`);
    };

    update();
    const interval = setInterval(update, 60000); // update every minute
    return () => clearInterval(interval);
  }, [isClockedIn, slot.clock_in_time]);

  const handleClockIn = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post(`/timeslots/${slot.id}/clock-in`);
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      queryClient.invalidateQueries({ queryKey: ['my-slots'] });
    } catch (err: any) {
      setError(err.message || 'Failed to clock in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post(`/timeslots/${slot.id}/clock-out`);
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      queryClient.invalidateQueries({ queryKey: ['my-slots'] });
    } catch (err: any) {
      setError(err.message || 'Failed to clock out');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format clock-in time for display
  const clockInDisplay = slot.clock_in_time
    ? new Date(slot.clock_in_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;

  // No Show
  if (isNoShow) {
    return (
      <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} className="text-red-400" />
          <span className="text-red-400 font-medium">No Show</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Scheduled: {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
        </p>
      </div>
    );
  }

  // Completed (clocked in and out)
  if (isCompleted) {
    return (
      <div className="mt-2 p-3 bg-slate-700/50 border border-slate-600 rounded-lg">
        <div className="flex items-center gap-2">
          <CheckCircle size={18} className="text-emerald-400" />
          <span className="text-emerald-400 font-medium">Completed</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Worked: {slot.actual_start_time ? formatTime12Hour(slot.actual_start_time) : '?'} - {slot.actual_end_time ? formatTime12Hour(slot.actual_end_time) : '?'}
          {slot.actual_hours_worked != null && ` (${slot.actual_hours_worked.toFixed(1)}h)`}
        </p>
      </div>
    );
  }

  // Currently clocked in — show clock out button
  if (isClockedIn) {
    return (
      <div className="mt-2 space-y-2">
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-blue-400 animate-pulse" />
            <span className="text-sm text-blue-400">
              Clocked in at {clockInDisplay} — {elapsed} elapsed
            </span>
          </div>
        </div>
        {isOwner && (
          <button
            onClick={handleClockOut}
            disabled={isSubmitting}
            data-testid={`clickable-clock-out-${slot.id}`}
            className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 text-lg"
          >
            {isSubmitting ? 'Clocking Out...' : 'Clock Out'}
          </button>
        )}
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    );
  }

  // Not yet clocked in — show clock in button (only for today, only for owner, only before slot ends)
  const nowHHMM = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Los_Angeles' });
  const slotEndPassed = nowHHMM > slot.end_time;
  if (isOwner && isToday && slot.status === 'claimed' && !slotEndPassed) {
    return (
      <div className="mt-2 space-y-2">
        <div className="p-2 bg-slate-700/50 rounded-lg">
          <p className="text-xs text-gray-400">
            Your shift: {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
          </p>
        </div>
        <button
          onClick={handleClockIn}
          disabled={isSubmitting}
          data-testid={`clickable-clock-in-${slot.id}`}
          className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 text-lg"
        >
          {isSubmitting ? 'Clocking In...' : 'Clock In'}
        </button>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    );
  }

  // Parent/admin viewing a slot — show status info only
  if (isParentOrAdmin && !isOwner) {
    if (slot.status === 'claimed' && isToday) {
      return (
        <div className="mt-2 p-2 bg-slate-700/50 rounded-lg">
          <p className="text-xs text-gray-400">
            Shift: {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)} — not yet clocked in
          </p>
        </div>
      );
    }
    return null;
  }

  return null;
}
